import { CheckCheck } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { SeoIssue } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { IssueDetailText } from './issue-detail-text';
import { IssueEntityLink } from './issue-entity-link';
import { SeverityBadge } from './severity-badge';

export interface SeoIssueColumnsOptions {
  storeId: string;
  resolvingId: string | null;
  onResolve: (issue: SeoIssue) => void;
}

/** Column model of the SEO issues table; `meta` ties columns to the list's sorts and filters. */
export function useSeoIssueColumns({ storeId, resolvingId, onResolve }: SeoIssueColumnsOptions) {
  const { t } = useTranslation('seo');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<SeoIssue>>>(
    () => [
      {
        id: 'severity',
        header: t('columns.severity'),
        cell: ({ row }) => <SeverityBadge severity={row.original.severity} />,
        meta: { label: t('columns.severity'), sortField: 'severity', filterKey: 'severity' },
      },
      {
        id: 'issue',
        header: t('columns.issue'),
        cell: ({ row }) => <IssueDetailText issue={row.original} />,
        meta: { label: t('columns.issue'), filterKey: 'code', lockVisibility: true },
      },
      {
        id: 'entity',
        header: t('columns.entity'),
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <IssueEntityLink storeId={storeId} issue={row.original} />
            <span className="text-muted-foreground text-xs">
              {t(`entityType.${row.original.entityType}`)}
            </span>
          </div>
        ),
        meta: { label: t('columns.entity'), filterKey: 'entityType' },
      },
      {
        id: 'seen',
        header: t('columns.lastSeen'),
        cell: ({ row }) => {
          const { lastSeenAt, firstSeenAt, resolvedAt } = row.original;
          return (
            <div className="flex flex-col gap-0.5 text-sm whitespace-nowrap">
              <span title={format.utc(lastSeenAt)}>{format.relative(lastSeenAt)}</span>
              <span className="text-muted-foreground text-xs" title={format.utc(firstSeenAt)}>
                {t('columns.firstSeen', { when: format.relative(firstSeenAt) })}
              </span>
              {resolvedAt ? (
                <span className="text-success text-xs" title={format.utc(resolvedAt)}>
                  {t('columns.resolvedAt', { when: format.relative(resolvedAt) })}
                </span>
              ) : null}
            </div>
          );
        },
        meta: { label: t('columns.lastSeen'), sortField: 'lastSeenAt', filterKey: 'status' },
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('columns.actions')}</span>,
        meta: { label: t('columns.actions'), align: 'end' },
        cell: ({ row }) =>
          row.original.resolvedAt ? null : (
            <Can permission="content.write">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={resolvingId === row.original.id}
                aria-label={t('actions.resolveIssue', {
                  code: t(`code.${row.original.code}`),
                  entity: row.original.handle ?? t(`entityType.${row.original.entityType}`),
                })}
                onClick={() => onResolve(row.original)}
              >
                <CheckCheck aria-hidden />
                {t('actions.resolve')}
              </Button>
            </Can>
          ),
      },
    ],
    [format, onResolve, resolvingId, storeId, t],
  );
}
